// Page principale de gestion de la Boutique Pro - Espace Client
import { useState, useEffect } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Store, Eye, Palette, Image, Star, Package, Bot, MessageSquare,
  ExternalLink, TrendingUp, Users, ShoppingBag, ArrowRight, CheckCircle
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { toast } from 'sonner';

import { API_URL } from '../../../config/api';

export default function BoutiqueDashboard() {
  const { user } = useOutletContext() || {};
  const [shopConfig, setShopConfig] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const token = localStorage.getItem('clientToken');

  useEffect(() => {
    fetchShopData();
  }, []);

  const fetchShopData = async () => {
    try {
      const [configRes, statsRes] = await Promise.all([
        fetch(`${API_URL}/api/vendor/shop-config`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/vendor/shop-stats`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      if (configRes.ok) {
        const config = await configRes.json();
        setShopConfig(config);
      }
      
      if (statsRes.ok) {
        const stats = await statsRes.json();
        setStats(stats);
      }
    } catch (e) {
      console.error('Error fetching shop data:', e);
    } finally {
      setLoading(false);
    }
  };

  const shopUrl = shopConfig?.slug 
    ? `${window.location.origin}/boutique/${shopConfig.slug}`
    : null;

  const isConfigured = shopConfig?.logo && shopConfig?.primary_color;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Ma Boutique</h1>
          <p className="text-gray-500 mt-1">
            Gère ta vitrine dans le Centre Commercial Virtuel
          </p>
        </div>
        
        {shopUrl && (
          <a href={shopUrl} target="_blank" rel="noopener noreferrer">
            <Button className="bg-orange-500 hover:bg-orange-600">
              <Eye size={18} className="mr-2" />
              Voir ma boutique
              <ExternalLink size={16} className="ml-2" />
            </Button>
          </a>
        )}
      </div>

      {/* Setup Progress */}
      {!isConfigured && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-2xl p-6 text-white"
        >
          <h2 className="text-xl font-bold mb-2">Configure ta boutique</h2>
          <p className="text-orange-100 mb-4">
            Personnalise ta boutique pour attirer plus de clients !
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div className={`flex items-center gap-3 p-3 rounded-lg ${shopConfig?.logo ? 'bg-white/20' : 'bg-white/10'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${shopConfig?.logo ? 'bg-green-400' : 'bg-white/20'}`}>
                {shopConfig?.logo ? <CheckCircle size={18} /> : <span>1</span>}
              </div>
              <span>Ajouter ton logo</span>
            </div>
            <div className={`flex items-center gap-3 p-3 rounded-lg ${shopConfig?.primary_color ? 'bg-white/20' : 'bg-white/10'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${shopConfig?.primary_color ? 'bg-green-400' : 'bg-white/20'}`}>
                {shopConfig?.primary_color ? <CheckCircle size={18} /> : <span>2</span>}
              </div>
              <span>Choisir tes couleurs</span>
            </div>
            <div className={`flex items-center gap-3 p-3 rounded-lg ${shopConfig?.hero_images?.length >= 1 ? 'bg-white/20' : 'bg-white/10'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${shopConfig?.hero_images?.length >= 1 ? 'bg-green-400' : 'bg-white/20'}`}>
                {shopConfig?.hero_images?.length >= 1 ? <CheckCircle size={18} /> : <span>3</span>}
              </div>
              <span>Ajouter des photos</span>
            </div>
          </div>
          
          <Link to="/espace-client/boutique/design">
            <Button className="bg-white text-orange-600 hover:bg-orange-50">
              Commencer la personnalisation
              <ArrowRight size={18} className="ml-2" />
            </Button>
          </Link>
        </motion.div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Eye size={20} className="text-blue-600" />
            </div>
            <span className="text-gray-500">Vues</span>
          </div>
          <p className="text-3xl font-bold text-gray-900">{stats?.views || 0}</p>
          <p className="text-sm text-green-600 mt-1">+12% ce mois</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-green-100 rounded-lg">
              <Users size={20} className="text-green-600" />
            </div>
            <span className="text-gray-500">Followers</span>
          </div>
          <p className="text-3xl font-bold text-gray-900">{stats?.followers || 0}</p>
          <p className="text-sm text-green-600 mt-1">+5 cette semaine</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-purple-100 rounded-lg">
              <MessageSquare size={20} className="text-purple-600" />
            </div>
            <span className="text-gray-500">Contacts</span>
          </div>
          <p className="text-3xl font-bold text-gray-900">{stats?.contacts || 0}</p>
          <p className="text-sm text-green-600 mt-1">+8 ce mois</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <Star size={20} className="text-yellow-600" />
            </div>
            <span className="text-gray-500">Note moyenne</span>
          </div>
          <p className="text-3xl font-bold text-gray-900">{stats?.rating || '-'}</p>
          <p className="text-sm text-gray-500 mt-1">{stats?.reviews_count || 0} avis</p>
        </motion.div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Link to="/espace-client/boutique/design">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            whileHover={{ scale: 1.02 }}
            className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:border-orange-300 cursor-pointer transition-colors"
          >
            <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center mb-4">
              <Palette size={24} className="text-orange-600" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-1">Personnalisation</h3>
            <p className="text-sm text-gray-500">Logo, couleurs et style</p>
          </motion.div>
        </Link>

        <Link to="/espace-client/boutique/medias">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            whileHover={{ scale: 1.02 }}
            className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:border-blue-300 cursor-pointer transition-colors"
          >
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
              <Image size={24} className="text-blue-600" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-1">Photos & Médias</h3>
            <p className="text-sm text-gray-500">Images hero et galerie</p>
          </motion.div>
        </Link>

        <Link to="/espace-client/ai-vendor/catalog">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            whileHover={{ scale: 1.02 }}
            className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:border-green-300 cursor-pointer transition-colors"
          >
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mb-4">
              <Package size={24} className="text-green-600" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-1">Catalogue</h3>
            <p className="text-sm text-gray-500">Produits et services</p>
          </motion.div>
        </Link>

        <Link to="/espace-client/boutique/avis">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            whileHover={{ scale: 1.02 }}
            className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:border-yellow-300 cursor-pointer transition-colors"
          >
            <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center mb-4">
              <Star size={24} className="text-yellow-600" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-1">Avis Clients</h3>
            <p className="text-sm text-gray-500">Gérer les témoignages</p>
          </motion.div>
        </Link>
      </div>

      {/* Preview Section */}
      {isConfigured && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Aperçu de ta boutique</h2>
            <a href={shopUrl} target="_blank" rel="noopener noreferrer">
              <Button variant="outline" size="sm">
                <ExternalLink size={16} className="mr-2" />
                Ouvrir
              </Button>
            </a>
          </div>
          
          <div className="aspect-video bg-gray-100 rounded-lg overflow-hidden">
            <iframe
              src={shopUrl}
              className="w-full h-full border-0"
              title="Aperçu boutique"
            />
          </div>
          
          <p className="text-sm text-gray-500 mt-3 text-center">
            URL de ta boutique : <a href={shopUrl} className="text-orange-600 hover:underline" target="_blank" rel="noopener noreferrer">{shopUrl}</a>
          </p>
        </motion.div>
      )}
    </div>
  );
}
